import { UpdatesCenter } from '@/axiosService';

class DownloadService {
  constructor() {
    // Nueva variable específica para el centro de actualizaciones
    // VUE_APP_UPDATECENTER=1 → Usar servidor real updates.centralgym.app
    // VUE_APP_UPDATECENTER=0 → Usar datos mock/simulados (por defecto)
    this.useRealServer = process.env.VUE_APP_UPDATECENTER === '1';
    this.useMockData = !this.useRealServer;
    
    // Usar la nueva instancia específica para el centro de actualizaciones
    this.api = UpdatesCenter;
    
    // Datos mock para desarrollo
    this.mockData = {
      pos: {
        latest: '2.1.4',
        mandatory: false,
        installer: '/downloads/centralgym-pos-installer-v2.1.4.exe',
        updater: '/downloads/centralgym-pos-updater-v2.1.4.exe'
      },
      access: {
        latest: '3.0.2',
        mandatory: true,
        installer: '/downloads/centralgym-access-installer-v3.0.2.exe',
        updater: '/downloads/centralgym-access-updater-v3.0.2.exe'
      }
    };
  }

  /**
   * Obtiene la información de releases para CentralGym POS
   * @returns {Promise<Object>} Información de la versión POS
   */
  async getPosReleases() {
    // Si se configuró para usar datos mock, evitar peticiones al servidor real
    if (this.useMockData) {
      console.log('🔧 Modo mock: usando datos simulados para POS releases');
      return new Promise((resolve) => {
        setTimeout(() => resolve(this.mockData.pos), 500); // Simular delay de red
      });
    }
    
    try {
      const response = await this.api.get('/centralgym/pos/releases.json');
      return response.data;
    } catch (error) {
      console.error('Error loading POS releases:', error);
      throw new Error('No se pudo cargar la información de CentralGym POS');
    }
  }

  /**
   * Obtiene la información de releases para CentralGym ACCESS
   * @returns {Promise<Object>} Información de la versión ACCESS
   */
  async getAccessReleases() {
    // Si se configuró para usar datos mock, evitar peticiones al servidor real
    if (this.useMockData) {
      console.log('🔧 Modo mock: usando datos simulados para ACCESS releases');
      return new Promise((resolve) => {
        setTimeout(() => resolve(this.mockData.access), 500); // Simular delay de red
      });
    }
    
    try {
      const response = await this.api.get('/centralgym/access/releases.json');
      return response.data;
    } catch (error) {
      console.error('Error loading ACCESS releases:', error);
      throw new Error('No se pudo cargar la información de CentralGym ACCESS');
    }
  }

  /**
   * Obtiene la información de releases para ambos productos
   * @returns {Promise<Object>} Información de ambas versiones
   */
  async getAllReleases() {
    try {
      const [posReleases, accessReleases] = await Promise.all([
        this.getPosReleases(),
        this.getAccessReleases()
      ]);

      return {
        pos: posReleases,
        access: accessReleases
      };
    } catch (error) {
      console.error('Error loading releases:', error);
      throw new Error('No se pudo cargar la información de las descargas. Verifique su conexión a internet.');
    }
  }

  /**
   * Verifica si hay actualizaciones obligatorias
   * @param {Object} releases - Releases obtenidos de getAllReleases
   * @returns {Boolean} True si hay actualizaciones obligatorias
   */
  hasMandatoryUpdates(releases) {
    return releases.pos.mandatory || releases.access.mandatory;
  }

  /**
   * Obtiene la URL completa para descarga directa
   * @param {String} relativeUrl - URL relativa del archivo
   * @returns {String} URL completa para descarga
   */
  getDownloadUrl(relativeUrl) {
    // Si ya es una URL completa, retornarla tal como está
    if (relativeUrl.startsWith('http')) {
      return relativeUrl;
    }
    
    // Si se configuró para usar datos mock, usar URLs simuladas
    if (this.useMockData) {
      console.log('🔧 Modo mock: usando URL simulada para descarga');
      // Retornar una URL que simule la descarga (puede ser un archivo local o placeholder)
      return `#mock-download${relativeUrl}`;
    }
    
    // Construir URL usando el centro de actualizaciones
    const baseUrl = this.api.defaults.baseURL;
    return `${baseUrl}${relativeUrl}`;
  }

  /**
   * Registra un evento de descarga
   * @param {String} product - Producto (pos, access)
   * @param {String} type - Tipo de descarga (installer, updater)
   * @param {String} version - Versión descargada
   */
  trackDownload(product, type, version) {
    const event = {
      product,
      type,
      version,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent
    };

    // Registrar en consola siempre
    console.log('Descarga iniciada:', event);

    // Si se configuró para usar datos mock, solo registrar en consola
    if (this.useMockData) {
      console.log('🔧 Modo mock: tracking simulado (no enviado al servidor)');
      return;
    }

    // Enviar evento de tracking al centro de actualizaciones
    this.api.post('/api/track/download', event).catch(error => {
      console.warn('Error al registrar descarga:', error);
    });
  }

  /**
   * Obtiene estadísticas de descargas
   * @returns {Promise<Object>} Estadísticas de descargas
   */
  async getDownloadStats() {
    // Si se configuró para usar datos mock, usar estadísticas simuladas
    if (this.useMockData) {
      console.log('🔧 Modo mock: usando estadísticas simuladas');
      return new Promise((resolve) => {
        setTimeout(() => resolve({
          totalDownloads: 1547,
          posDownloads: 892,
          accessDownloads: 655,
          lastUpdate: new Date().toISOString()
        }), 300);
      });
    }
    
    try {
      const response = await this.api.get('/api/stats');
      return response.data;
    } catch (error) {
      console.error('Error loading download stats:', error);
      throw new Error('No se pudo cargar las estadísticas de descargas');
    }
  }


}

export default new DownloadService(); 